import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import iconChat from './assets/icon_chat.png';
import axios from 'axios';

function CommunityChat({ currentUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Функция скролла с выбором типа анимации
  const scrollToBottom = (behavior = 'smooth') => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior });
    }, 50);
  };

  const fetchMessages = async (isInitialLoad = false) => {
    try {
      const res = await api.get('chat/messages/');
      setMessages(res.data);
      
      // Если это первая загрузка при открытии чата — скроллим мгновенно вниз
      if (isInitialLoad) {
        scrollToBottom('auto');
      }
    } catch (err) {
      console.error("Ошибка загрузки сообщений:", err);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Вы уверены, что хотите удалить это сообщение?")) return;

    try {
      await api.delete(`chat/messages/${messageId}/`);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/chat/messages/${messageId}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      } catch (err) {
        console.error('Ошибка при удалении:', err);
        alert('Не удалось удалить сообщение.');
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMessages(true); // Запрашиваем с флагом первичной загрузки
      const interval = setInterval(() => fetchMessages(false), 3000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      alert("Файл слишком большой! Максимум 15 МБ.");
      return;
    }

    setSelectedFile(file);
    setFilePreview(URL.createObjectURL(file));
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() && !selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('text', text);
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const res = await api.post('chat/messages/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessages(prev => [...prev, res.data]);
      setText('');
      clearSelectedFile();
      scrollToBottom('smooth'); // Плавный скролл при отправке своего сообщения
    } catch (err) {
      alert("Не удалось отправить сообщение.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 font-sans">
      
      {/* 1. КНОПКА ОТКРЫТИЯ ЧАТА В СТИЛЕ LIQUID GLASS */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="relative bg-slate-900/60 backdrop-blur-2xl border border-white/15 hover:border-white/30 p-3.5 rounded-3xl shadow-[0_15px_35px_rgba(0,0,0,0.4)] transition-all duration-300 hover:scale-105 active:scale-95 flex items-center gap-3 cursor-pointer group"
        >
          <img 
            src={iconChat} 
            alt="Чат" 
            className="w-12 h-12 object-contain drop-shadow-md group-hover:rotate-6 transition-transform" 
          />
          <span className="text-sm font-bold text-slate-200 pr-2 hidden sm:inline tracking-wide">
            Чат
          </span>
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
          </span>
        </button>
      )}

      {/* 2. ОКНО ЧАТА В СТИЛЕ LIQUID GLASS */}
      {isOpen && (
        <div className="w-[360px] sm:w-[400px] h-[540px] bg-slate-900/80 backdrop-blur-2xl border border-white/15 rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          
          {/* Хедер чата */}
          <div className="p-4 bg-slate-950/40 backdrop-blur-md text-white flex items-center justify-between border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              <div>
                <h3 className="font-bold text-sm tracking-wide leading-none text-slate-100">Чат сообщества</h3>
                <span className="text-[10px] text-slate-400 font-medium">Онлайн</span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white p-1 transition cursor-pointer text-lg"
            >
              ✕
            </button>
          </div>

          {/* Список сообщений */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-slate-950/20 custom-scrollbar">
            {messages.length === 0 ? (
              <div className="text-center text-slate-400 text-xs my-auto py-12">
                💬 Чат пуст. Напишите первым!
              </div>
            ) : (
              messages.map((msg) => {
                const msgAuthorName = msg.username || msg.author || msg.user?.username;
                const isMe = currentUser?.username && currentUser?.username === msgAuthorName;
                
                const canDelete = Boolean(
                  currentUser?.is_staff || 
                  currentUser?.is_superuser ||
                  (currentUser?.id && (currentUser?.id === msg.user || currentUser?.id === msg.user?.id)) ||
                  isMe
                );

                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 px-1">
                      <span className="text-[10px] font-bold text-slate-400">
                        {msgAuthorName || 'Гость'}
                      </span>
                      {msg.is_staff && (
                        <span className="text-[9px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-1 rounded font-semibold">
                          Admin
                        </span>
                      )}
                    </div>

                    <div className="relative flex items-center gap-1.5 max-w-[90%]">
                      <div
                        className={`p-3 rounded-2xl text-xs font-medium leading-relaxed shadow-sm transition-all ${
                          isMe
                            ? 'bg-indigo-600/80 backdrop-blur-md border border-indigo-400/30 text-white rounded-br-none shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                            : 'bg-slate-800/50 backdrop-blur-md border border-white/10 text-slate-200 rounded-bl-none shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]'
                        }`}
                      >
                        {/* Картинка / Видео в сообщении */}
                        {msg.file && (
                          <div className="mb-2 rounded-xl overflow-hidden border border-white/10 shadow-inner">
                            {msg.file.match(/\.(mp4|webm|ogg)$/i) ? (
                              <video src={msg.file} controls className="max-h-48 w-full object-cover" />
                            ) : (
                              <img 
                                src={msg.file} 
                                alt="Вложение" 
                                className="max-h-48 w-full object-cover cursor-pointer hover:opacity-95 transition" 
                                onClick={() => window.open(msg.file, '_blank')} 
                              />
                            )}
                          </div>
                        )}

                        {msg.text && <p className="whitespace-pre-wrap break-words">{msg.text}</p>}
                      </div>

                      {/* КНОПКА УДАЛЕНИЯ */}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(msg.id)}
                          className="bg-rose-500/20 hover:bg-rose-600/80 border border-rose-500/30 text-rose-300 hover:text-white p-1 rounded-lg transition-all cursor-pointer text-[11px] flex items-center justify-center min-w-[22px] min-h-[22px] backdrop-blur-sm"
                          title="Удалить сообщение"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Превью выбранного файла перед отправкой */}
          {filePreview && (
            <div className="px-4 py-2 bg-slate-900/60 backdrop-blur-md border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-xs">📎</span>
                <span className="text-xs font-bold text-slate-300 truncate max-w-[200px]">
                  {selectedFile?.name}
                </span>
              </div>
              <button onClick={clearSelectedFile} className="text-xs text-rose-400 font-bold hover:text-rose-300 cursor-pointer transition">
                Удалить
              </button>
            </div>
          )}

          {/* Поле ввода в стиле Liquid Glass */}
          <form onSubmit={handleSend} className="p-3 bg-slate-950/40 backdrop-blur-md border-t border-white/10 flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,video/*"
              className="hidden"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2.5 text-slate-400 hover:text-indigo-300 hover:bg-white/5 transition rounded-xl cursor-pointer text-lg shrink-0"
              title="Прикрепить фото или видео"
            >
              📎
            </button>

            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Написать сообщение..."
              className="flex-1 bg-slate-900/50 backdrop-blur-sm border border-white/10 rounded-2xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/60 shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] transition-colors"
            />

            <button
              type="submit"
              disabled={uploading || (!text.trim() && !selectedFile)}
              className="bg-indigo-600/80 hover:bg-indigo-500/90 disabled:opacity-30 disabled:hover:bg-indigo-600/80 text-white border border-indigo-400/30 p-2.5 rounded-2xl transition-all active:scale-95 cursor-pointer flex items-center justify-center font-bold text-xs shrink-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]"
            >
              {uploading ? '...' : '➔'}
            </button>
          </form>

        </div>
      )}
    </div>
  );
}

export default CommunityChat;